/**
 * 微信环境视频自动播放 Hook
 * 用于解决 iOS 微信浏览器的自动播放限制
 *
 * 原理：利用用户首次手势交互"解锁"视频播放权限
 * 一旦解锁，后续可以程序化控制播放/暂停
 */

export const useVideoAutoplay = () => {
  const isUnlocked = ref(false);
  const videoElements = new Set<HTMLVideoElement>();
  let hasSetupListeners = false;
  const events = ['touchstart', 'touchend', 'click', 'scroll'] as const;

  // 检测是否是微信浏览器
  const isWeixin = computed(() => {
    if (import.meta.server || typeof window === 'undefined') return false;
    return /MicroMessenger/i.test(navigator.userAgent);
  });

  // 检测是否是 iOS
  const isIOS = computed(() => {
    if (import.meta.server || typeof window === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  });

  // 是否需要解锁（仅 iOS 微信需要）
  const needsUnlock = computed(() => {
    return isIOS.value && isWeixin.value;
  });

  /**
   * 解锁单个视频元素的播放权限
   */
  const unlockVideo = (video: HTMLVideoElement) => {
    return new Promise<void>(resolve => {
      video
        .play()
        .then(() => {
          video.pause();
          console.debug('Video unlocked:', video.src);
          resolve();
        })
        .catch(error => {
          console.debug('Video unlock failed:', error.message);
          resolve(); // 即使失败也 resolve，不阻塞流程
        });
    });
  };

  const removeUnlockListeners = () => {
    if (import.meta.server || typeof document === 'undefined' || !hasSetupListeners) return;
    events.forEach(event => {
      document.removeEventListener(event, handleUnlock);
    });
    hasSetupListeners = false;
  };

  /**
   * 解锁所有已注册的视频元素
   */
  const unlockAllVideos = async () => {
    if (isUnlocked.value || videoElements.size === 0) {
      return;
    }

    console.debug(`Unlocking ${videoElements.size} videos...`);

    // 并发解锁所有视频
    const unlockPromises = Array.from(videoElements).map(video => unlockVideo(video));

    await Promise.all(unlockPromises);
    isUnlocked.value = true;
    console.debug('All videos unlocked');
  };

  /**
   * 全局解锁处理函数
   */
  const handleUnlock = async () => {
    await unlockAllVideos();
    if (isUnlocked.value) {
      removeUnlockListeners();
    }
  };

  /**
   * 注册视频元素
   */
  const registerVideo = (video: HTMLVideoElement | null) => {
    if (!video) return;

    videoElements.add(video);

    // 如果不需要解锁（非 iOS 微信），直接标记为已解锁
    if (!needsUnlock.value) {
      isUnlocked.value = true;
    }

    // 如果已经解锁过了，立即解锁新注册的视频
    if (isUnlocked.value && needsUnlock.value) {
      unlockVideo(video);
      return;
    }

    if (needsUnlock.value) {
      setupUnlockListeners();
    }
  };

  /**
   * 注销视频元素
   */
  const unregisterVideo = (video: HTMLVideoElement | null) => {
    if (!video) return;
    videoElements.delete(video);
  };

  /**
   * 初始化全局解锁监听
   * 只需调用一次即可
   */
  const setupUnlockListeners = () => {
    // SSR 环境下不执行
    if (import.meta.server || typeof document === 'undefined') return;

    if (!needsUnlock.value) {
      isUnlocked.value = true;
      return;
    }

    if (isUnlocked.value || hasSetupListeners) {
      return;
    }

    events.forEach(event => {
      document.addEventListener(event, handleUnlock, { passive: true });
    });
    hasSetupListeners = true;

    console.debug('Video unlock listeners setup');
  };

  /**
   * 清理监听器
   */
  const cleanup = () => {
    // SSR 环境下不执行
    if (import.meta.server || typeof document === 'undefined') {
      videoElements.clear();
      return;
    }

    removeUnlockListeners();
    videoElements.clear();
  };

  return {
    isUnlocked: readonly(isUnlocked),
    needsUnlock: readonly(needsUnlock),
    registerVideo,
    unregisterVideo,
    setupUnlockListeners,
    unlockAllVideos,
    cleanup,
  };
};

/**
 * 创建全局单例实例
 * 确保整个应用共享同一个解锁状态
 */
let globalInstance: ReturnType<typeof useVideoAutoplay> | null = null;

export const useGlobalVideoAutoplay = () => {
  // SSR 环境下返回一个空实现，避免跨请求共享状态
  if (import.meta.server) {
    return {
      isUnlocked: readonly(ref(false)),
      needsUnlock: readonly(ref(false)),
      registerVideo: () => {},
      unregisterVideo: () => {},
      setupUnlockListeners: () => {},
      unlockAllVideos: async () => {},
      cleanup: () => {},
    };
  }

  if (!globalInstance) {
    globalInstance = useVideoAutoplay();
    // 在应用卸载时清理全局实例
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        globalInstance?.cleanup();
        globalInstance = null;
      });
    }
  }
  return globalInstance;
};
